<template>
	<v-row class="full-height find-related-orders-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="find-related-orders-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="3" lg="2">
							<v-text-field
								v-model="searchForm.styleCode"
								placeholder="Search style code..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="find-related-orders-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Date Filter Toggle -->
						<v-col cols="auto">
							<v-btn
								depressed
								small
								color="primary"
								@click="showDateFilter = !showDateFilter"
							>
								{{ showDateFilter ? 'Hide Date Filter' : 'Date Filter' }}
							</v-btn>
						</v-col>

						<!-- Start Date -->
						<v-col cols="6" sm="3" md="2" v-if="showDateFilter">
							<v-menu
								v-model="showStartDate"
								:close-on-content-click="false"
								transition="scale-transition"
								offset-y
								min-width="290px"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										v-model="formattedStartDate"
										label="From"
										append-icon="mdi-calendar"
										@click:append="showStartDate = true"
										outlined
										dense
										v-bind="attrs"
										v-on="on"
										@blur="searchForm.startDate = parseDate(formattedStartDate)"
										hide-details
									></v-text-field>
								</template>
								<v-date-picker
									color="primary"
									v-model="searchForm.startDate"
									@input="showStartDate = false"
								></v-date-picker>
							</v-menu>
						</v-col>

						<!-- End Date -->
						<v-col cols="6" sm="3" md="2" v-if="showDateFilter">
							<v-menu
								v-model="showEndDate"
								:close-on-content-click="false"
								transition="scale-transition"
								offset-y
								min-width="290px"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										v-model="formattedEndDate"
										label="To"
										append-icon="mdi-calendar"
										@click:append="showEndDate = true"
										outlined
										dense
										v-bind="attrs"
										v-on="on"
										@blur="searchForm.endDate = parseDate(formattedEndDate)"
										hide-details
									></v-text-field>
								</template>
								<v-date-picker
									color="primary"
									v-model="searchForm.endDate"
									@input="showEndDate = false"
								></v-date-picker>
							</v-menu>
						</v-col>

						<!-- Filter Checkboxes -->
						<v-col cols="auto">
							<v-checkbox
								v-model="searchForm.filterQuotes"
								label="Quotes"
								hide-details
								dense
								class="mt-0 mr-2"
								@change="onFilterChange"
							></v-checkbox>
						</v-col>
						<v-col cols="auto">
							<v-checkbox
								v-model="searchForm.filterOrders"
								label="Orders"
								hide-details
								dense
								class="mt-0"
								@change="onFilterChange"
							></v-checkbox>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 find-related-orders-table-container">
			<v-data-table
				:headers="headers"
				:items="salesOrderLineItemDetails"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="find-related-orders-data-table"
				must-sort
				fixed-header
			>
				<!-- Style Code Column -->
				<template v-slot:item.styleCode="{ item }">
					<div class="table-icon">
						<SystemCategoryIcon :system-category-id="item.systemCategoryId" />
						{{ item.styleCode }}
					</div>
				</template>

				<!-- Type Column -->
				<template v-slot:item.type="{ item }">
					<template v-if="item.type === 'Quote'">
						<v-icon small class="mr-1">mdi-message-processing-outline</v-icon>
						Quote
					</template>
					<template v-else>
						<v-icon small class="mr-1">mdi-shopping</v-icon>
						Order
					</template>
				</template>

				<!-- Quote Number Column -->
				<template v-slot:item.quoteNumber="{ item }">
					<router-link
						v-if="item.quoteId"
						:to="{ path: `/sales-orders/quotes/${item.quoteId}` }"
						class="link-blue"
					>
						{{ item.quoteNumber }}
					</router-link>
				</template>

				<!-- Order Number Column -->
				<template v-slot:item.orderNumber="{ item }">
					<router-link
						v-if="item.orderId"
						:to="{ path: `/sales-orders/orders/${item.orderId}` }"
						class="link-blue"
					>
						{{ item.orderNumber }}
					</router-link>
				</template>

				<!-- Sales Rep Column -->
				<template v-slot:item.salesRepId="{ item }">
					{{ renderSalesRep(item.salesRepId) }}
				</template>

				<!-- Date Column -->
				<template v-slot:item.date="{ item }">
					{{ formatDate(item.date) }}
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
						<p class="mt-2 grey--text">No related orders found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";

export default {
	name: "FindRelatedOrders",
	components: {
		SystemCategoryIcon,
	},
	data() {
		return {
			loading: false,
			showDateFilter: false,
			showStartDate: false,
			formattedStartDate: this.formatDate(
				new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10)
			),
			showEndDate: false,
			formattedEndDate: this.formatDate(new Date().toISOString().slice(0, 10)),
			searchForm: {
				styleCode: "",
				startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
				endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
					.toISOString()
					.slice(0, 10),
				filterQuotes: false,
				filterOrders: false,
			},
			salesReps: [],
			salesOrderLineItemDetails: [],
			totalCount: 0,
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["date"],
				sortDesc: [true],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Style Code",
					value: "styleCode",
					sortable: true,
					width: "26%",
				},
				{
					text: "Type",
					value: "type",
					sortable: true,
					width: "10%",
				},
				{
					text: "Quote #",
					value: "quoteNumber",
					sortable: true,
					width: "13%",
				},
				{
					text: "Order #",
					value: "orderNumber",
					sortable: true,
					width: "13%",
				},
				{
					text: "Rep",
					value: "salesRepId",
					sortable: true,
					width: "18%",
				},
				{
					text: "Order Date",
					value: "date",
					sortable: true,
					width: "20%",
				},
			],
		};
	},
	apollo: {
		salesReps: {
			query() {
				return gql`
					query SalesReps {
						salesReps(take: 500, order: { name: ASC }) {
							items {
								salesRepId
								salesRepNumber
								name
								inactiveDate
								sequence
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			update: (data) => data.salesReps.items,
		},
		lineItemsQuery: {
			query() {
				return gql`
					query GetLineItems(
						$styleCode: String
						$startDate: DateTime
						$endDate: DateTime
						$offset: Int!
						$take: Int!
						$order: [SalesOrderLineItemDetailsSortInput!] = [{ date: DESC }]
						$filterQuotes: Boolean!
						$filterOrders: Boolean!
					) {
						searchOrderLineItemsByStyleCode(
							styleCode: $styleCode
							startDate: $startDate
							endDate: $endDate
							order: $order
							skip: $offset
							take: $take
							filterQuotes: $filterQuotes
							filterOrders: $filterOrders
						) {
							totalCount
							items {
								type
								quoteLineItemId
								orderLineItemId
								parentLineItemId
								itemId
								systemCategoryId
								systemCategory
								itemType
								styleCode
								description
								price
								customerId
								organization
								quoteId
								quoteNumber
								orderId
								orderNumber
								status
								salesRepId
								date
								note
								unitPrice
								quantity
								dropShip
								includePrice
								lettering_LetterPositionId
								lettering_Text
								braidPosition
							}
							pageInfo {
								hasNextPage
								hasPreviousPage
							}
						}
					}
				`;
			},
			variables() {
				return this.buildQueryVariables();
			},
			update(data) {
				this.loading = false;
				this.totalCount = data.searchOrderLineItemsByStyleCode.totalCount;
				this.salesOrderLineItemDetails = data.searchOrderLineItemsByStyleCode.items;
				return data.searchOrderLineItemsByStyleCode;
			},
			error(error) {
				console.error("Error fetching line items:", error);
				this.loading = false;
			},
		},
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		"searchForm.startDate"(value) {
			this.formattedStartDate = this.formatDate(value);
			this.onFilterChange();
		},
		"searchForm.endDate"(value) {
			this.formattedEndDate = this.formatDate(value);
			this.onFilterChange();
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/findRelatedOrdersState"];
					if (savedState) {
						// Restore page as 1-indexed for v-data-table
						this.$set(this.tableOptions, "page", Math.floor((savedState.offset || 0) / 15) + 1);
						this.$set(this.searchForm, "styleCode", savedState.styleCode || "");
						this.$set(this.searchForm, "startDate", savedState.startDate);
						this.$set(this.searchForm, "endDate", savedState.endDate);
						this.$set(this.searchForm, "filterQuotes", savedState.filterQuotes || false);
						this.$set(this.searchForm, "filterOrders", savedState.filterOrders || false);
						this.showDateFilter = savedState.showDateFilter || false;

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		formatDate(date) {
			if (!date) return "";
			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		parseDate(date) {
			if (!date) return null;
			const [month, day, year] = date.split("/");
			return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
		},

		renderSalesRep(salesRepId) {
			let repById = this.salesReps.find((r) => r.salesRepId == salesRepId);
			return repById ? repById.name : null;
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;

			// Build sort object based on selected column
			const sortField = sortBy[0] || "date";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const order = [{ [sortField]: sortDirection }];

			return {
				styleCode: this.searchForm.styleCode || "",
				startDate: this.searchForm.startDate,
				endDate: this.searchForm.endDate,
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				filterQuotes: this.searchForm.filterQuotes,
				filterOrders: this.searchForm.filterOrders,
			};
		},

		async fetchData() {
			this.loading = true;
			if (this.$apollo.queries.lineItemsQuery) {
				await this.$apollo.queries.lineItemsQuery.refetch();
			}
		},

		clearSearch() {
			this.searchForm = {
				styleCode: "",
				startDate: null,
				endDate: null,
				filterQuotes: false,
				filterOrders: false,
			};
			this.formattedStartDate = "";
			this.formattedEndDate = "";
		},
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveFindRelatedOrdersState", {
			// Store as offset for backwards compatibility
			offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
			styleCode: this.searchForm.styleCode,
			startDate: this.searchForm.startDate,
			endDate: this.searchForm.endDate,
			filterQuotes: this.searchForm.filterQuotes,
			filterOrders: this.searchForm.filterOrders,
			showDateFilter: this.showDateFilter,
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.find-related-orders-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.find-related-orders-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.find-related-orders-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.find-related-orders-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.find-related-orders-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 900px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}

	// Style Code icon styling
	.table-icon {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	// Link styling
	.link-blue {
		color: $color-blue;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
}
</style>
